/**
 * Data tools — ADMIN only (28 Aug 2026).
 *
 * One operation: replace the program data with the examples transcribed from
 * Parinaam_Volunteering_Activity_Attributes.docx.
 *
 * The CSV exports lived here briefly and now have their own screen. They did
 * not belong: this page is built around a control nobody should reach by
 * accident, and downloading a list is routine, non-destructive work. Sharing a
 * door meant either the exports inherited a warning they did not warrant or the
 * reset was softened by the company it kept. See `ExportsPage`.
 *
 * ---------------------------------------------------------------------------
 * Why this is a screen of its own rather than a button on the dashboard
 * ---------------------------------------------------------------------------
 * It erases every program, activity and attendance record. Putting that next
 * to the figures staff read every morning means the one destructive control in
 * the product sits where the most traffic is, and the only thing between a bad
 * day and an empty database is a click landing where it was aimed. A separate
 * page costs a deliberate navigation step, which is the point.
 *
 * The confirmation is typed rather than a yes/no, for the same reason: a dialog
 * with a confirm button is answered reflexively by anyone who has answered a
 * hundred of them. Typing the word is a decision.
 *
 * The screen renders nothing at all where the installation has not enabled the
 * operation — see `useDemoDataStatus`.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  TextInput,
  useToast,
} from '../../components/common';
import { useDemoDataStatus, useResetDemoData } from '../../hooks/useDemoData';
import { useAuth } from '../../hooks/useAuth';
import type { DemoDataCounts } from '../../services/api/demo-data';
import styles from './DataToolsPage.module.css';

/** What the admin has to type. Lower-cased on comparison, not on display. */
const CONFIRM_WORD = 'reset';

const ROWS: Array<{ key: keyof DemoDataCounts; label: string }> = [
  { key: 'programs', label: 'Programs' },
  { key: 'activities', label: 'Activities' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'attendance', label: 'Attendance records' },
];

export function DataToolsPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');

  const status = useDemoDataStatus(isAdmin);
  const reset = useResetDemoData();
  const toast = useToast();

  const [typed, setTyped] = useState('');
  const [result, setResult] = useState<{ before: DemoDataCounts; after: DemoDataCounts } | null>(
    null
  );

  const confirmed = typed.trim().toLowerCase() === CONFIRM_WORD;

  function onReset() {
    if (!confirmed) return;
    reset.mutate(undefined, {
      onSuccess: (data) => {
        setResult(data);
        setTyped('');
        toast.success('Program data replaced with the client document examples.');
      },
      onError: (error) => toast.error(error, 'The reset could not be completed.'),
    });
  }

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Data tools" />
        <Card>
          <EmptyState
            title="Admins only"
            message="These tools need an admin account."
          />
        </Card>
      </>
    );
  }

  if (status.isPending) return <LoadingState label="Checking what is available…" />;

  if (status.isError) {
    return (
      <>
        <PageHeader title="Data tools" />
        <Card>
          <ErrorState error={status.error} onRetry={() => void status.refetch()} />
        </Card>
      </>
    );
  }

  if (!status.data?.reset_available) {
    return (
      <>
        <PageHeader title="Data tools" />
        <Card>
          <EmptyState
            title="Nothing to do here"
            message={
              'Resetting the demonstration data is switched off on this installation. ' +
              'It is enabled per environment with ALLOW_DEMO_DATA_RESET, which is how an ' +
              'installation holding real volunteering history stays safe from it.'
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Data tools" />

      <Card title="Reset the demonstration data">
        <div className={styles.body}>
          <p>
            This removes{' '}
            <strong>every program, activity, session, assignment and attendance record</strong>,
            then seeds the four programs from the client document — Exposure Visit, Read to Rise,
            Chote Kadam and the day outings — with the sessions each one ran and the hours recorded
            against them.
          </p>
          <p className={styles.spared}>
            Volunteers and accounts are not touched.
          </p>
          {/*
            Said on the screen rather than left to the seed script’s output,
            because this is the answer to the question the reset provokes: the
            rosters afterwards name six people, and somebody has to be able to
            sign in as one of them to see what the reset produced.
          */}
          <p className={styles.spared}>
            Rosters rebuilt on the six demo volunteers.
          </p>
          <p className={styles.warning}>
            This cannot be undone.
          </p>

          <div className={styles.confirm}>
            <TextInput
              label={`Type ${CONFIRM_WORD} to confirm`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              disabled={reset.isPending}
            />
            <Button
              variant="danger"
              onClick={onReset}
              disabled={!confirmed || reset.isPending}
              loading={reset.isPending}
            >
              Reset demonstration data
            </Button>
          </div>
        </div>
      </Card>

      {result && (
        <Card title="What changed">
          <div className={styles.countsScroll}>
            <table className={styles.counts}>
              <thead>
                <tr>
                  <th scope="col">&nbsp;</th>
                  <th scope="col">Before</th>
                  <th scope="col">After</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    <td>{result.before[row.key]}</td>
                    <td>{result.after[row.key]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
